import { defineStore } from 'pinia';
import { getChildren, updateChildName } from '@/services/authService';
import { accountApi } from '@/services/accountApi';
import { useUserStore } from '@/stores/userStore';

export const useStudentStore = defineStore('studentStore', {
  state: () => ({
    students: [],
    activeStudent: null,
    loading: false,
    childLimit: { current: 0, max: 1, plan: 'free' },
  }),

  getters: {
    atChildLimit(state) {
      return state.students.length >= state.childLimit.max;
    },
  },

  actions: {
    setStudents(list) {
      this.students = list;
    },

    setActiveStudent(id) {
      this.activeStudent = id;
    },

    /**
     * Load children for the given parent uid.
     * Primary source: MongoDB via accountApi.
     * Falls back to localStorage (authService) if the API is unavailable,
     * so the app works offline / during local development without the Lambda.
     */
    async loadChildren(uid) {
      this.loading = true;
      try {
        const { children, limit } = await accountApi.getChildren(uid);
        this.students = children.map(c => ({
          id: c.childId,
          name: c.name,
          track: 'eplus_core',
          scheduledArchiveAt: c.scheduledArchiveAt || null,
        }));
        if (limit) {
          this.childLimit = limit;
        }
      } catch (e) {
        console.warn('[studentStore] API unavailable, falling back to localStorage:', e);
        const children = getChildren(uid);
        this.students = children.map(c => ({
          id: c.id,
          name: c.name,
          track: 'eplus_core',
        }));
      } finally {
        this.loading = false;
      }
    },

    /**
     * Rename a child in the local store and persist to MongoDB.
     * Also keeps localStorage in sync as a fallback.
     */
    renameChild(childId, newName) {
      const trimmed = newName.trim();
      if (!trimmed) return;

      const child = this.students.find(s => s.id === childId);
      if (child) child.name = trimmed;

      const uid = useUserStore().session?.uid;
      if (uid) {
        // Persist to MongoDB
        accountApi.updateChild(uid, childId, trimmed)
          .catch(e => console.warn('[studentStore] Failed to rename child in API:', e));
        // Keep localStorage in sync (fallback / offline)
        updateChildName(uid, childId, trimmed);
      }
    },

    logout() {
      this.activeStudent = null;
      this.students = [];
    },
  },
});
